import { useState } from 'react'
import {
  Box,
  Typography,
  Button,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Card,
  CardContent,
  CardActions,
  Grid,
  Tabs,
  Tab,
  LinearProgress,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Skeleton,
  Container,
  Stack,
} from '@mui/material'
import {
  Add,
  Edit,
  Visibility,
  Assignment,
  Folder,
  Schedule,
  ArrowBack,
  ArrowForward,
  SwapHoriz,
  Upload,
  Search,
  Description,
  Download,
  Delete,
  Timeline,
  CalendarToday,
  VideoCall,
  People,
  Groups,
  OpenInNew,
  CheckCircle,
} from '@mui/icons-material'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import { useAppSelector } from '../hooks/redux'
import {
  useGetCasesQuery,
  useCreateCaseMutation,
  useUpdateCaseMutation,
  useDeleteCaseMutation,
  useAssignCaseMutation,
  useUnassignTeamFromCaseMutation,
  useGetCaseQuery,
  useGetEvidencePoolSummaryQuery,
  useMoveToNextRoundMutation,
  useMoveToRoundMutation,
  type Case,
  type CaseCreate,
} from '../store/api/casesApi'
import { useGetTeamsQuery } from '../store/api/teamsApi'
import {
  useGetCaseRoundsQuery,
  useUpdateRoundMutation,
  type Round,
  RoundType,
  RoundStatus,
} from '../store/api/roundsApi'
import { hasPermission } from '../utils/permissions'
import { 
  useGetDocumentsQuery,
  useUploadDocumentMutation,
  useDeleteDocumentMutation,
  useUpdateDocumentMutation,
  useLazyDownloadDocumentQuery,
  useSearchDocumentsMutation,
  useSearchDocumentsScoringMutation,
  DocumentType,
  EvidenceLabel,
} from '../store/api/documentsApi'
import { formatCaseDisplay } from '../utils/caseDisplay'
import { gradientBrand } from '../theme/brand'
import { PageHeader } from '../components/PageHeader'
import { getTablePaperSx } from '../theme/sectionSurfaces'
import { getTeamRegistrationSummary, getTeamRegistrationLabel } from '../utils/teamRegistration'
import {
  useGetCourtroomByRoundQuery,
  useJoinCourtroomMutation,
  useEndCourtroomMutation,
  useCreateCourtroomMutation,
  CourtroomStatus,
} from '../store/api/courtroomsApi'

const STATUS_OPTIONS = [
  { value: 'draft', label: 'Bản nháp' },
  { value: 'assigned', label: 'Đã phân bổ' },
  { value: 'in_progress', label: 'Đang thực hiện' },
  { value: 'submitted', label: 'Đã nộp bài' },
  { value: 'scored', label: 'Đã chấm điểm' },
]

const STATUS_COLORS: Record<string, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
  draft: 'default',
  assigned: 'info',
  in_progress: 'primary',
  submitted: 'warning',
  scored: 'success',
}

const CASE_TYPE_NAMES: Record<string, string> = {
  'dân_sự': 'Dân sự',
  'hình_sự': 'Hình sự',
  'hành_chính': 'Hành chính',
  'lao_động': 'Lao động',
  'thương_mại': 'Thương mại',
  'hôn_nhân_gia_đình': 'Hôn nhân & Gia đình',
  'khác': 'Khác',
}

const getCaseTypeName = (caseType?: string): string => {
  if (!caseType) return '-'
  return CASE_TYPE_NAMES[caseType] || caseType
}

export default function CasesPage() {
  const navigate = useNavigate()
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: cases = [], isLoading, refetch } = useGetCasesQuery()
  const { data: teams = [] } = useGetTeamsQuery()
  const [createCase, { isLoading: isCreating }] = useCreateCaseMutation()
  const [updateCase, { isLoading: isUpdating }] = useUpdateCaseMutation()
  const [deleteCase] = useDeleteCaseMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [viewDialog, setViewDialog] = useState(false)
  const [editingCase, setEditingCase] = useState<Case | null>(null)
  const [viewingCase, setViewingCase] = useState<Case | null>(null)
  const [formData, setFormData] = useState<CaseCreate>({
    title: '',
    parties_requests: '',
    description: '',
    case_number: '',
    case_type: '',
    court: '',
    plaintiff: '',
    defendant: '',
    incident_date: '',
    filing_date: '',
    dispute_value: '',
    legal_area: '',
    summary: '',
    deadline: '',
  })
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Check permissions
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  const canViewAllCases = currentUser && hasPermission(currentUser.role, 'VIEW_CASES')
  const canViewOwnCase = currentUser && hasPermission(currentUser.role, 'VIEW_OWN_CASE')

  // Get user's team for filtering
  const userTeam = teams.find(t => t.members.includes(currentUser?.id || ''))

  // Filter cases based on permissions
  const displayCases = canViewAllCases
    ? cases
    : canViewOwnCase && userTeam
      ? cases.filter(c => c.assigned_team_ids && c.assigned_team_ids.includes(userTeam.id))
      : []

  const handleOpenDialog = (caseItem?: Case) => {
    if (caseItem) {
      setEditingCase(caseItem)
      setFormData({
        title: caseItem.title,
        parties_requests: caseItem.parties_requests || '',
        description: caseItem.description || '',
        case_number: caseItem.case_number || '',
        case_type: caseItem.case_type || '',
        court: caseItem.court || '',
        plaintiff: caseItem.plaintiff || '',
        defendant: caseItem.defendant || '',
        incident_date: caseItem.incident_date ? new Date(caseItem.incident_date).toISOString().slice(0, 16) : '',
        filing_date: caseItem.filing_date ? new Date(caseItem.filing_date).toISOString().slice(0, 16) : '',
        dispute_value: caseItem.dispute_value || '',
        legal_area: caseItem.legal_area || '',
        summary: caseItem.summary || '',
        materials_list: caseItem.materials_list || '',
        submission_requirements: caseItem.submission_requirements || '',
        deadline: caseItem.deadline ? new Date(caseItem.deadline).toISOString().slice(0, 16) : '',
        // Dual flow — load tên claimant 2 hồ sơ (fix: trước đây không load → save bị reset null).
        scenario_a_claimant_name: (caseItem as any).scenario_a_claimant_name ?? null,
        scenario_b_claimant_name: (caseItem as any).scenario_b_claimant_name ?? null,
      })
    } else {
      setEditingCase(null)
      setFormData({
        title: '',
        parties_requests: '',
        description: '',
        case_number: '',
        case_type: '',
        court: '',
        plaintiff: '',
        defendant: '',
        incident_date: '',
        filing_date: '',
        dispute_value: '',
        legal_area: '',
        summary: '',
        deadline: '',
      })
    }
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingCase(null)
    setFormData({
      title: '',
      description: '',
      case_number: '',
      case_type: '',
      court: '',
      plaintiff: '',
      defendant: '',
      incident_date: '',
      filing_date: '',
      dispute_value: '',
      legal_area: '',
      summary: '',
      materials_list: '',
      submission_requirements: '',
      deadline: '',
    })
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleViewCase = (caseItem: Case) => {
    setViewingCase(caseItem)
    setViewDialog(true)
  }

  const handleCloseViewDialog = () => {
    setViewDialog(false)
    setViewingCase(null)
  }

  // Delete case handler
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [caseToDelete, setCaseToDelete] = useState<Case | null>(null)

  const handleDeleteClick = (e: React.MouseEvent, caseItem: Case) => {
    e.stopPropagation()
    setCaseToDelete(caseItem)
    setDeleteConfirmOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!caseToDelete) return

    try {
      await deleteCase(caseToDelete.id).unwrap()
      setSuccessMessage('Xóa cuộc thi thành công!')
      refetch()
    } catch (error: any) {
      setErrorMessage(`Xóa cuộc thi thất bại: ${error.data?.detail || error.message}`)
    } finally {
      setDeleteConfirmOpen(false)
      setCaseToDelete(null)
    }
  }

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false)
    setCaseToDelete(null)
  }

  const handleSubmit = async () => {
    setErrorMessage(null)
    setSuccessMessage(null)

    if (!formData.title.trim()) {
      setErrorMessage('Tiêu đề cuộc thi là bắt buộc')
      return
    }

    try {
      // Prepare data with proper datetime format
      const submitData: CaseCreate = {
        ...formData,
        // Convert datetime-local format to ISO string if present
        incident_date: formData.incident_date ? new Date(formData.incident_date).toISOString() : undefined,
        filing_date: formData.filing_date ? new Date(formData.filing_date).toISOString() : undefined,
        deadline: formData.deadline ? new Date(formData.deadline).toISOString() : undefined,
      }

      if (editingCase) {
        await updateCase({ id: editingCase.id, ...submitData }).unwrap()
        setSuccessMessage('Cập nhật cuộc thi thành công!')
      } else {
        await createCase(submitData).unwrap()
        setSuccessMessage('Tạo cuộc thi thành công!')
      }
      handleCloseDialog()
      refetch()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }


  // Get assigned team names
  const getAssignedTeamNames = (teamIds?: string[]) => {
    if (!teamIds || teamIds.length === 0) return '-'
    const teamNames = teamIds
      .map(teamId => {
        const team = teams.find(t => t.id === teamId)
        return team ? team.name : null
      })
      .filter(Boolean)
    return teamNames.length > 0 ? teamNames.join(', ') : '-'
  }

  const assignedCaseCount = displayCases.filter((caseItem) => (caseItem.assigned_team_ids || []).length > 0).length
  const assignedTeamTotal = displayCases.reduce((total, caseItem) => total + (caseItem.assigned_team_ids?.length || 0), 0)
  const upcomingDeadlineCount = displayCases.filter((caseItem) => {
    if (!caseItem.deadline) return false
    return new Date(caseItem.deadline).getTime() >= Date.now()
  }).length
  const scoredCaseCount = displayCases.filter((caseItem) => caseItem.status === 'scored').length
  const caseStats = [
    { label: 'Tổng cuộc thi', value: displayCases.length, icon: <Folder fontSize="small" color="primary" /> },
    { label: 'Đã phân đội', value: assignedCaseCount, icon: <Assignment fontSize="small" color="success" /> },
    { label: 'Lượt đội được phân', value: assignedTeamTotal, icon: <Groups fontSize="small" color="secondary" /> },
    { label: 'Deadline sắp tới', value: upcomingDeadlineCount, icon: <Schedule fontSize="small" color="warning" /> },
    { label: 'Đã chấm điểm', value: scoredCaseCount, icon: <CheckCircle fontSize="small" color="success" /> },
  ]

  return (
    <Container maxWidth="xl" sx={{ mt: { xs: 2, sm: 3 }, mb: { xs: 2, sm: 4 }, px: { xs: 2, sm: 3 } }}>
      <PageHeader
        title={canAssignCases ? 'Quản lý Cuộc thi' : 'Cuộc thi của tôi'}
        subtitle={
          canAssignCases
            ? 'Tạo cuộc thi, phân đội, theo dõi vòng và tài liệu liên quan.'
            : 'Các cuộc thi được gán cho đội của bạn.'
        }
        icon={<Folder />}
        actions={
          canAssignCases ? (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => handleOpenDialog()}
              sx={{
                background: gradientBrand,
                '&:hover': {
                  background: gradientBrand,
                  filter: 'brightness(1.06)',
                },
              }}
            >
              Tạo cuộc thi mới
            </Button>
          ) : null
        }
      />

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {canAssignCases && (
        <Box
          sx={{
            display: 'grid',
            gap: 2,
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(2, minmax(0, 1fr))',
              lg: 'repeat(5, minmax(0, 1fr))',
            },
            mb: 3,
          }}
        >
          {caseStats.map((item) => (
            <Paper
              key={item.label}
              elevation={0}
              sx={(theme) => ({
                p: 2,
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                minHeight: 86,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              })}
            >
              <Box
                sx={{
                  width: 38,
                  height: 38,
                  borderRadius: 1.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  backgroundColor: 'action.hover',
                  flexShrink: 0,
                }}
              >
                {item.icon}
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} lineHeight={1.1}>
                  {item.value}
                </Typography>
                <Typography variant="caption" color="text.secondary" fontWeight={600}>
                  {item.label}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Box>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : displayCases.length === 0 ? (
        <Paper
          elevation={0}
          sx={(theme) => ({
            p: { xs: 4, md: 6 },
            textAlign: 'center',
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
          })}
        >
          <Folder sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chưa có cuộc thi nào
          </Typography>
          {canAssignCases && (
            <Typography variant="body2" color="text.secondary">
              Hãy tạo cuộc thi mới để bắt đầu
            </Typography>
          )}
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0} sx={(theme) => getTablePaperSx(theme)}>
          <Table sx={{ minWidth: 980 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'background.default', whiteSpace: 'nowrap' } }}>
                <TableCell sx={{ minWidth: 330 }}><strong>Tiêu đề cuộc thi</strong></TableCell>
                <TableCell sx={{ minWidth: 140 }}><strong>Loại cuộc thi</strong></TableCell>
                <TableCell sx={{ minWidth: 160 }}><strong>Số đội được phân</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Deadline</strong></TableCell>
                <TableCell sx={{ minWidth: 150 }}><strong>Trạng thái</strong></TableCell>
                <TableCell
                  align="right"
                  sx={{
                    minWidth: 280,
                    position: { md: 'sticky' },
                    right: 0,
                    zIndex: 2,
                    borderLeft: '1px solid',
                    borderLeftColor: 'divider',
                  }}
                >
                  <strong>Thao tác</strong>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayCases.map((caseItem) => (
                <TableRow
                  key={caseItem.id}
                  sx={{
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                  }}
                >
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Folder fontSize="small" color="primary" />
                      <Box>
                        <Typography variant="body1" fontWeight={500}>
                          {formatCaseDisplay(caseItem)}
                        </Typography>
                        {caseItem.parties_requests && (
                          <Tooltip title={caseItem.parties_requests}>
                            <Typography 
                              variant="caption" 
                              color="text.secondary" 
                              sx={{ 
                                display: 'block',
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {caseItem.parties_requests.length > 80 
                                ? `${caseItem.parties_requests.substring(0, 80)}...` 
                                : caseItem.parties_requests}
                            </Typography>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {caseItem.case_type ? (
                      <Chip 
                        label={getCaseTypeName(caseItem.case_type)} 
                        size="small" 
                        variant="outlined"
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {caseItem.assigned_team_ids && caseItem.assigned_team_ids.length > 0 ? (
                      <Tooltip title={getAssignedTeamNames(caseItem.assigned_team_ids)}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Assignment fontSize="small" color="action" />
                          <Typography variant="body2">
                            {caseItem.assigned_team_ids.length} đội
                          </Typography>
                        </Box>
                      </Tooltip>
                    ) : (
                      <Typography variant="body2" color="text.secondary">Chưa phân bổ</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    {caseItem.deadline ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Schedule fontSize="small" color="action" />
                        <Typography variant="body2">
                          {new Date(caseItem.deadline).toLocaleDateString('vi-VN', {
                            day: '2-digit',
                            month: '2-digit',
                            year: 'numeric'
                          })}
                        </Typography>
                      </Box>
                    ) : (
                      <Typography variant="body2" color="text.secondary">-</Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={STATUS_OPTIONS.find(s => s.value === caseItem.status)?.label || caseItem.status}
                      color={STATUS_COLORS[caseItem.status] || 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={{
                      position: { md: 'sticky' },
                      right: 0,
                      bgcolor: 'background.paper',
                      borderLeft: '1px solid',
                      borderLeftColor: 'divider',
                    }}
                  >
                    <Stack direction="row" spacing={1} justifyContent="flex-end" flexWrap="wrap" useFlexGap>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Description />}
                        onClick={(e) => {
                          e.stopPropagation()
                          navigate(`/cases/${caseItem.id}/exam`)
                        }}
                        sx={{ textTransform: 'none' }}
                      >
                        Xem đề thi
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<Visibility />}
                        onClick={(e) => {
                          e.stopPropagation()
                          handleViewCase(caseItem)
                        }}
                        sx={{
                          '&:hover': {
                            backgroundColor: 'primary.light',
                            color: 'white',
                          }
                        }}
                      >
                        Xem chi tiết
                      </Button>
                      {/* E.2 — Edit/Delete chỉ secretary (ASSIGN_CASES) — judge KHÔNG. */}
                      {canAssignCases && (
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleOpenDialog(caseItem)
                          }}
                          color="primary"
                          title="Chỉnh sửa cuộc thi"
                        >
                          <Edit />
                        </IconButton>
                      )}
                      {canAssignCases && (
                        <IconButton
                          size="small"
                          onClick={(e) => handleDeleteClick(e, caseItem)}
                          color="error"
                          title="Xóa cuộc thi"
                        >
                          <Delete />
                        </IconButton>
                      )}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Create/Edit Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingCase ? 'Chỉnh sửa cuộc thi' : 'Tạo cuộc thi mới'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2.5} sx={{ pt: 1 }}>
            <Typography variant="subtitle1" fontWeight={800}>Thông tin cơ bản</Typography>
            <TextField
              label="Tiêu đề cuộc thi"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              fullWidth
            />
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Số vụ án"
                value={formData.case_number}
                onChange={(e) => setFormData({ ...formData, case_number: e.target.value })}
                fullWidth
              />
              <FormControl fullWidth>
                <InputLabel>Loại cuộc thi</InputLabel>
                <Select
                  value={formData.case_type}
                  onChange={(e) => setFormData({ ...formData, case_type: e.target.value })}
                  label="Loại cuộc thi"
                >
                  <MenuItem value="dân_sự">Dân sự</MenuItem>
                  <MenuItem value="hình_sự">Hình sự</MenuItem>
                  <MenuItem value="hành_chính">Hành chính</MenuItem>
                  <MenuItem value="lao_động">Lao động</MenuItem>
                  <MenuItem value="thương_mại">Thương mại</MenuItem>
                  <MenuItem value="hôn_nhân_gia_đình">Hôn nhân & Gia đình</MenuItem>
                  <MenuItem value="khác">Khác</MenuItem>
                </Select>
              </FormControl>
            </Box>
            <TextField
              label="Tòa án xử lý"
              value={formData.court}
              onChange={(e) => setFormData({ ...formData, court: e.target.value })}
              fullWidth
            />

            {/* YC5 (audit 2026-05-23): nghiệp vụ hiện tại = 2 NGUYÊN ĐƠN
                (không phải nguyên đơn vs bị đơn). Section này gộp với phần
                "Tên nguyên đơn 2 hồ sơ" ở dưới — đồng bộ field plaintiff →
                scenario_a_claimant_name, defendant → scenario_b_claimant_name
                để KHÔNG dirty data cũ. UI giờ chỉ render 1 lần ở section
                "Hai nguyên đơn (Vòng Sơ loại)". */}

            <Typography variant="subtitle1" fontWeight={800}>Thông tin thời gian</Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Ngày xảy ra sự kiện"
                type="datetime-local"
                value={formData.incident_date}
                onChange={(e) => setFormData({ ...formData, incident_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
              <TextField
                label="Ngày khởi kiện"
                type="datetime-local"
                value={formData.filing_date}
                onChange={(e) => setFormData({ ...formData, filing_date: e.target.value })}
                InputLabelProps={{ shrink: true }}
                fullWidth
              />
            </Box>

            <Typography variant="subtitle1" fontWeight={800}>Thông tin khác</Typography>
            <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' } }}>
              <TextField
                label="Giá trị tranh chấp"
                value={formData.dispute_value}
                onChange={(e) => setFormData({ ...formData, dispute_value: e.target.value })}
                placeholder="Ví dụ: 500.000.000 VNĐ"
                fullWidth
              />
              <TextField
                label="Lĩnh vực pháp luật"
                value={formData.legal_area}
                onChange={(e) => setFormData({ ...formData, legal_area: e.target.value })}
                placeholder="Ví dụ: Hợp đồng, Bất động sản, v.v."
                fullWidth
              />
            </Box>
            <TextField
              label="Tóm tắt cuộc thi"
              value={formData.summary}
              onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
              multiline
              rows={3}
              fullWidth
            />
            <TextField
              label="Yêu cầu các bên"
              value={formData.parties_requests}
              onChange={(e) => setFormData({ ...formData, parties_requests: e.target.value })}
              multiline
              rows={4}
              placeholder="Nhập yêu cầu của nguyên đơn và bị đơn..."
              fullWidth
              helperText="Mô tả chi tiết sẽ được quản lý qua upload tài liệu"
            />
            {/* UAT-TC #7: Yêu cầu bài nộp cho đề thi (danh mục tài liệu lấy từ tab Tài liệu cuộc thi) */}
            <TextField
              label="Yêu cầu bài nộp cho đề thi này *"
              value={formData.submission_requirements || ''}
              onChange={(e) => setFormData({ ...formData, submission_requirements: e.target.value })}
              multiline
              rows={6}
              fullWidth
              placeholder={
                'VD:\n' +
                '- Memorial: PDF/DOCX, tối đa 25 trang, font Times New Roman 13, line-spacing 1.5\n' +
                '- Trích dẫn theo APA, có mục lục và danh mục tài liệu tham khảo\n' +
                '- Chứng cứ: tối đa 4 file (PDF/JPG/PNG/MP4), mỗi file ≤ 100MB\n' +
                '- Ngày kết thúc cuộc thi: 23:59 ngày 30/06/2026'
              }
              helperText="Quy định bắt buộc đội thi phải tuân thủ khi nộp memorial / chứng cứ. Hiển thị ở mục 3 trang Đề thi."
            />

            {/* YC4 (audit 2026-05-23): bỏ trường "Ngày kết thúc cuộc thi"
                khỏi form tạo/sửa cuộc thi. Thời gian chỉ set ở badge "Vòng thi"
                (mỗi round có start_date / end_date riêng). */}

            {/* Tên nguyên đơn 2 hồ sơ (Vòng Sơ loại — dual flow mặc định).
                Section "Flow R1 / Shortlist / Final advancing" đã xóa: Phase B
                refactor bỏ legacy_single_role, shortlist size cố định ở reports
                workflow, chung kết cố định 2 đội ở publishRound. */}
            <Typography variant="subtitle1" fontWeight={800}>Hai nguyên đơn (Vòng Sơ loại)</Typography>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
              <TextField
                label="Nguyên đơn Hồ sơ 1"
                value={formData.scenario_a_claimant_name ?? formData.plaintiff ?? ''}
                onChange={(e) => {
                  // YC5: đồng bộ scenario_a + plaintiff (legacy field).
                  setFormData({
                    ...formData,
                    scenario_a_claimant_name: e.target.value,
                    plaintiff: e.target.value,
                  })
                }}
                fullWidth
                placeholder="VD: Nguyễn Thanh"
                helperText="Hiển thị: 'Hồ sơ 1 — {tên}'"
              />
              <TextField
                label="Nguyên đơn Hồ sơ 2"
                value={formData.scenario_b_claimant_name ?? formData.defendant ?? ''}
                onChange={(e) => {
                  // YC5: đồng bộ scenario_b + defendant (legacy field — nay
                  // mang ý nghĩa "nguyên đơn 2", giữ tương thích DB cũ).
                  setFormData({
                    ...formData,
                    scenario_b_claimant_name: e.target.value,
                    defendant: e.target.value,
                  })
                }}
                fullWidth
                placeholder="VD: Trần Bích Hạ"
                helperText="Hiển thị: 'Hồ sơ 2 — {tên}'"
              />
            </Stack>
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleCloseDialog}>Hủy</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={isCreating || isUpdating}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            {isCreating || isUpdating ? <CircularProgress size={20} /> : (editingCase ? 'Cập nhật' : 'Tạo')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Case Dialog */}
      {viewingCase && (
        <CaseDetailDialog
          open={viewDialog}
          onClose={handleCloseViewDialog}
          caseItem={viewingCase}
          teams={teams}
          onCaseUpdated={refetch}
        />
      )}

      {/* Delete Confirm Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc muốn xóa cuộc thi "{caseToDelete?.title}"?
            Hành động này sẽ xóa tất cả các vòng thi liên quan và không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  )
}

// Case Detail Dialog Component
function CaseDetailDialog({ 
  open, 
  onClose, 
  caseItem, 
  teams,
  onCaseUpdated
}: { 
  open: boolean
  onClose: () => void
  caseItem: Case
  teams: any[]
  onCaseUpdated?: () => void
}) {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const [tabValue, setTabValue] = useState(0)
  const canViewAllCases = currentUser && hasPermission(currentUser.role, 'VIEW_CASES')
  const canScoreCases = currentUser && hasPermission(currentUser.role, 'SCORE_CASES')
  const canAssignCases = currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES')
  
  // Refetch case data
  const { data: currentCaseData, refetch: refetchCase } = useGetCaseQuery(caseItem.id, { skip: !open })
  const [assignCase, { isLoading: isAssigning }] = useAssignCaseMutation()
  const [unassignTeamFromCase, { isLoading: isUnassigning }] = useUnassignTeamFromCaseMutation()
  // Pool đáp án — BTC kiểm tra trước khi assign cho đội (chống quên tag CORRECT/DECOY)
  const { data: poolSummary } = useGetEvidencePoolSummaryQuery(caseItem.id, { skip: !open })
  
  // Assignment dialog state
  const [assignDialogOpen, setAssignDialogOpen] = useState(false)
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>(caseItem.assigned_team_ids || [])
  const [assignDeadline, setAssignDeadline] = useState<string>(
    caseItem.deadline ? new Date(caseItem.deadline).toISOString().slice(0, 16) : ''
  )
  const [assignSuccessMessage, setAssignSuccessMessage] = useState<string | null>(null)
  const [assignErrorMessage, setAssignErrorMessage] = useState<string | null>(null)
  
  // Assign case function - matches moduleFunctions.ts
  const handleAssignCase = async () => {
    setAssignErrorMessage(null)
    setAssignSuccessMessage(null)
    
    if (selectedTeamIds.length === 0) {
      setAssignErrorMessage('Vui lòng chọn ít nhất một đội thi')
      return
    }
    
    try {
      await assignCase({
        caseId: caseItem.id,
        assignment: {
          team_ids: selectedTeamIds,
          deadline: assignDeadline ? new Date(assignDeadline).toISOString() : undefined,
        },
      }).unwrap()
      setAssignDialogOpen(false)
      await refetchCase()
      // Refetch danh sách cases bên ngoài
      if (onCaseUpdated) {
        onCaseUpdated()
      }
      setAssignSuccessMessage('Phân bổ đội thi thành công!')
      // Auto close success message after 3 seconds
      setTimeout(() => setAssignSuccessMessage(null), 3000)
    } catch (error: any) {
      setAssignErrorMessage(`Phân bổ thất bại: ${error.data?.detail || error.message}`)
    }
  }
  
  // Documents
  const { data: documents = [], isLoading: isLoadingDocs } = useGetDocumentsQuery(
    { case_id: caseItem.id },
    { skip: !open || tabValue !== 2 }
  )
  const [uploadDocument, { isLoading: isUploading }] = useUploadDocumentMutation()
  const [deleteDocument] = useDeleteDocumentMutation()
  const [updateDocument, { isLoading: isUpdatingDoc }] = useUpdateDocumentMutation()
  const [downloadDocument] = useLazyDownloadDocumentQuery()

  // Upload form state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    document_type: DocumentType.CASE_DOCUMENT,
    file: null as File | null,
  })
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false)
  const [editDocDialogOpen, setEditDocDialogOpen] = useState(false)
  const [editDocForm, setEditDocForm] = useState<{ id: string; title: string; description: string } | null>(null)

  const openEditDocDialog = (doc: { id: string; title: string; description?: string }) => {
    setEditDocForm({ id: doc.id, title: doc.title, description: doc.description || '' })
    setEditDocDialogOpen(true)
  }

  const handleUpdateDoc = async () => {
    if (!editDocForm) return
    if (!editDocForm.title.trim()) {
      alert('Tiêu đề không được để trống')
      return
    }
    try {
      await updateDocument({
        id: editDocForm.id,
        title: editDocForm.title.trim(),
        description: editDocForm.description.trim(),
      }).unwrap()
      setEditDocDialogOpen(false)
      setEditDocForm(null)
      alert('Cập nhật tài liệu thành công!')
    } catch (error: any) {
      alert(`Cập nhật thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleUpload = async () => {
    if (!uploadForm.file || !uploadForm.title) {
      alert('Vui lòng điền đầy đủ thông tin')
      return
    }

    const formData = new FormData()
    formData.append('file', uploadForm.file)
    formData.append('title', uploadForm.title)
    if (uploadForm.description) formData.append('description', uploadForm.description)
    formData.append('document_type', uploadForm.document_type)
    formData.append('case_id', caseItem.id)

    try {
      await uploadDocument(formData).unwrap()
      setUploadDialogOpen(false)
      setUploadForm({ title: '', description: '', document_type: DocumentType.CASE_DOCUMENT, file: null })
      alert('Upload thành công!')
    } catch (error: any) {
      alert(`Upload thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleDownload = async (docId: string, fileName: string) => {
    try {
      const result = await downloadDocument(docId).unwrap()
      const url = window.URL.createObjectURL(result)
      const a = document.createElement('a')
      a.href = url
      a.download = fileName
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch (error: any) {
      alert(`Download thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const handleDelete = async (docId: string) => {
    if (!confirm('Bạn có chắc muốn xóa tài liệu này?')) return
    try {
      await deleteDocument(docId).unwrap()
      alert('Xóa thành công!')
    } catch (error: any) {
      alert(`Xóa thất bại: ${error.data?.detail || error.message}`)
    }
  }

  const caseDocuments = documents.filter(
    (doc) => doc.document_type === DocumentType.CASE_DOCUMENT || doc.document_type === DocumentType.REFERENCE
  )

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Folder />
          <Typography variant="h6">{formatCaseDisplay(caseItem)}</Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        {assignSuccessMessage && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setAssignSuccessMessage(null)}>
            {assignSuccessMessage}
          </Alert>
        )}
        {assignErrorMessage && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setAssignErrorMessage(null)}>
            {assignErrorMessage}
          </Alert>
        )}
        <Tabs value={tabValue} onChange={(_, v) => setTabValue(v)} sx={{ mb: 2 }}>
          <Tab label="Thông tin" />
          {/* E.2 — Tab Vòng thi + Tài liệu chỉ secretary (ASSIGN_CASES). Judge xem như student → chỉ tab Thông tin. */}
          {canAssignCases && <Tab label="Vòng thi" />}
          {canAssignCases && <Tab label="Tài liệu cuộc thi" />}
        </Tabs>

        {/* Tab 1: Thông tin */}
        {tabValue === 0 && (() => {
          const assignedTeamIds =
            currentCaseData?.assigned_team_ids || caseItem.assigned_team_ids || []
          const fmtDate = (d?: string | Date) =>
            d ? new Date(d).toLocaleString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'
          const statusOpt = STATUS_OPTIONS.find((s) => s.value === caseItem.status)
          const deadlineDate = caseItem.deadline ? new Date(caseItem.deadline) : null
          const deadlineExpired = deadlineDate ? deadlineDate.getTime() < Date.now() : false

          // Section helper
          const SectionCard = ({
            title,
            icon,
            children,
            sx,
          }: {
            title: string
            icon?: React.ReactNode
            children: React.ReactNode
            sx?: object
          }) => (
            <Paper
              elevation={0}
              variant="outlined"
              sx={{ p: 2.5, borderRadius: 2, ...sx }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                {icon}
                <Typography
                  variant="overline"
                  fontWeight={700}
                  sx={{ letterSpacing: 1, color: 'text.secondary' }}
                >
                  {title}
                </Typography>
              </Box>
              {children}
            </Paper>
          )

          // Compact key-value row
          const Field = ({ label, value }: { label: string; value: React.ReactNode }) => (
            <Box sx={{ mb: 1.25 }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.4, fontWeight: 600 }}>
                {label}
              </Typography>
              <Box sx={{ mt: 0.25 }}>
                {typeof value === 'string' ? (
                  <Typography variant="body2" fontWeight={500}>{value}</Typography>
                ) : (
                  value
                )}
              </Box>
            </Box>
          )

          return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>
              {/* HERO HEADER */}
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  background: gradientBrand,
                  color: '#fff',
                  position: 'relative',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    {caseItem.case_number && (
                      <Typography variant="caption" sx={{ letterSpacing: 2, opacity: 0.85 }}>
                        {caseItem.case_number}
                      </Typography>
                    )}
                    <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1.3, mt: 0.5 }}>
                      {caseItem.title}
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75, mt: 1.5 }}>
                      {caseItem.case_type && (
                        <Chip
                          size="small"
                          label={getCaseTypeName(caseItem.case_type)}
                          sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 500 }}
                        />
                      )}
                      {caseItem.legal_area && (
                        <Chip
                          size="small"
                          label={caseItem.legal_area}
                          sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 500 }}
                        />
                      )}
                      {caseItem.court && (
                        <Chip
                          size="small"
                          label={caseItem.court}
                          sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', fontWeight: 500 }}
                        />
                      )}
                    </Box>
                  </Box>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 1 }}>
                    <Chip
                      label={statusOpt?.label || caseItem.status}
                      size="medium"
                      sx={{
                        bgcolor: '#fff',
                        color: 'primary.main',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: 0.5,
                      }}
                    />
                    {deadlineDate && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" sx={{ opacity: 0.85, display: 'block' }}>
                          {deadlineExpired ? 'Đã quá hạn' : 'Ngày kết thúc'}
                        </Typography>
                        <Typography variant="body2" fontWeight={600}>
                          {fmtDate(caseItem.deadline)}
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Paper>

              {/* GRID — 2 cột lớn */}
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
                {/* Left col */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <SectionCard title="Hai nguyên đơn" icon={<People fontSize="small" color="primary" />}>
                    <Field
                      label="Nguyên đơn Hồ sơ 1"
                      value={(caseItem as any).scenario_a_claimant_name || caseItem.plaintiff || '—'}
                    />
                    <Field
                      label="Nguyên đơn Hồ sơ 2"
                      value={(caseItem as any).scenario_b_claimant_name || caseItem.defendant || '—'}
                    />
                    {caseItem.dispute_value && (
                      <Field
                        label="Giá trị tranh chấp"
                        value={
                          <Chip
                            size="small"
                            label={caseItem.dispute_value}
                            color="warning"
                            variant="outlined"
                          />
                        }
                      />
                    )}
                  </SectionCard>

                  <SectionCard title="Thời gian" icon={<Schedule fontSize="small" color="primary" />}>
                    <Field label="Ngày xảy ra sự kiện" value={fmtDate(caseItem.incident_date)} />
                    <Field label="Ngày khởi kiện" value={fmtDate(caseItem.filing_date)} />
                    <Field
                      label="Ngày kết thúc cuộc thi"
                      value={
                        deadlineDate ? (
                          <Chip
                            size="small"
                            label={fmtDate(caseItem.deadline)}
                            color={deadlineExpired ? 'error' : 'success'}
                            variant="outlined"
                          />
                        ) : (
                          '—'
                        )
                      }
                    />
                    <Field label="Ngày tạo" value={fmtDate(caseItem.created_at)} />
                  </SectionCard>

                  <SectionCard
                    title="Phân bổ đội thi"
                    icon={<Groups fontSize="small" color="primary" />}
                  >
                    {canAssignCases && (
                      <Button
                        fullWidth
                        size="small"
                        variant="outlined"
                        startIcon={<Assignment />}
                        onClick={() => {
                          setSelectedTeamIds(assignedTeamIds)
                          setAssignDeadline(
                            currentCaseData?.deadline
                              ? new Date(currentCaseData.deadline).toISOString().slice(0, 16)
                              : caseItem.deadline
                                ? new Date(caseItem.deadline).toISOString().slice(0, 16)
                                : '',
                          )
                          setAssignDialogOpen(true)
                        }}
                        sx={{ mb: 2, textTransform: 'none' }}
                      >
                        {assignedTeamIds.length > 0 ? 'Chỉnh sửa phân bổ' : 'Phân bổ đội thi'}
                      </Button>
                    )}
                    {assignedTeamIds.length > 0 ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        {assignedTeamIds.map((teamId) => {
                          const team = teams.find((t) => t.id === teamId)
                          if (!team) return null
                          return (
                            <Box
                              key={teamId}
                              sx={{
                                p: 1.25,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 1.5,
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>
                                  {team.name}
                                </Typography>
                                <Typography variant="caption" color="text.secondary" noWrap>
                                  {team.university}
                                </Typography>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Chip
                                  size="small"
                                  label={STATUS_OPTIONS.find((s) => s.value === team.status)?.label || team.status}
                                  color={STATUS_COLORS[team.status] || 'default'}
                                  variant="outlined"
                                />
                                {canAssignCases && (
                                  <Tooltip title="Gỡ đội khỏi phân bổ">
                                    <span>
                                      <IconButton
                                        size="small"
                                        color="error"
                                        disabled={isUnassigning}
                                        onClick={async () => {
                                          const ok = window.confirm(
                                            `Gỡ "${team.name}" khỏi phân bổ cuộc thi?\n\n` +
                                            'Lưu ý: nếu đội đã nộp bản chính thức (Đơn khởi kiện/Chứng cứ), ' +
                                            'BE sẽ chặn — hãy reset bài nộp trước.',
                                          )
                                          if (!ok) return
                                          try {
                                            await unassignTeamFromCase({
                                              caseId: caseItem.id,
                                              teamId,
                                            }).unwrap()
                                          } catch (e: any) {
                                            alert(
                                              `Gỡ thất bại: ${e?.data?.detail || e?.message || ''}`,
                                            )
                                          }
                                        }}
                                      >
                                        <Delete fontSize="small" />
                                      </IconButton>
                                    </span>
                                  </Tooltip>
                                )}
                              </Box>
                            </Box>
                          )
                        })}
                      </Box>
                    ) : (
                      <Alert severity="warning" variant="outlined" sx={{ py: 0.5 }}>
                        Chưa có đội thi nào được phân bổ.
                      </Alert>
                    )}
                  </SectionCard>
                </Box>

                {/* Right col */}
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                  {caseItem.summary && (
                    <SectionCard title="Tóm tắt vụ án" icon={<Description fontSize="small" color="primary" />}>
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          color: 'text.primary',
                        }}
                      >
                        {caseItem.summary}
                      </Typography>
                    </SectionCard>
                  )}

                  {caseItem.parties_requests && (
                    <SectionCard
                      title="Yêu cầu các bên"
                      icon={<Folder fontSize="small" color="primary" />}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.7,
                          color: 'text.primary',
                        }}
                      >
                        {caseItem.parties_requests}
                      </Typography>
                    </SectionCard>
                  )}
                </Box>
              </Box>
            </Box>
          )
        })()}

        {/* Tab 2: Vòng thi (chỉ Secretary/Support — judge KHÔNG thấy) */}
        {tabValue === 1 && canAssignCases && (
          <CaseRoundsTab caseId={caseItem.id} caseItem={caseItem} />
        )}

        {/* Tab 3: Tài liệu cuộc thi (chỉ Secretary/Support — judge KHÔNG thấy) */}
        {tabValue === 2 && canAssignCases && (
          <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Tài liệu cuộc thi</Typography>
              <Button
                variant="contained"
                startIcon={<Upload />}
                onClick={() => setUploadDialogOpen(true)}
              >
                Upload Tài liệu
              </Button>
            </Box>
            
            {isLoadingDocs ? (
              <CircularProgress />
            ) : caseDocuments.length === 0 ? (
              <Alert severity="info">Chưa có tài liệu nào</Alert>
            ) : (
              (() => {
                // Group docs theo evidence_label cho secretary thấy rõ tách bạch
                const groups: { key: string; label: string; color: 'default' | 'success' | 'error' | 'info'; docs: typeof caseDocuments }[] = [
                  {
                    key: 'exam',
                    label: 'Đề bài & Tài liệu nền (không vào pool đội thi)',
                    color: 'info',
                    docs: caseDocuments.filter((d) => !d.evidence_label),
                  },
                  {
                    key: 'correct',
                    label: 'Chứng cứ chuẩn (đội nên chọn)',
                    color: 'success',
                    docs: caseDocuments.filter((d) => d.evidence_label === EvidenceLabel.CORRECT),
                  },
                  {
                    key: 'decoy',
                    label: 'Bẫy nhiễu (đội KHÔNG nên chọn)',
                    color: 'error',
                    docs: caseDocuments.filter((d) => d.evidence_label === EvidenceLabel.DECOY),
                  },
                ]
                const renderTable = (docs: typeof caseDocuments) => (
                  <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ width: '4%' }}></TableCell>
                          <TableCell><strong>Tên tài liệu</strong></TableCell>
                          <TableCell sx={{ width: '12%' }}><strong>Kích thước</strong></TableCell>
                          <TableCell sx={{ width: '22%' }}><strong>Nhãn BTC</strong></TableCell>
                          <TableCell align="right" sx={{ width: '14%' }}><strong>Thao tác</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {docs.map((doc) => (
                          <TableRow key={doc.id} hover>
                            <TableCell>
                              <Description fontSize="small" color="action" />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" noWrap title={doc.title}>{doc.title}</Typography>
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {doc.file_name}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="caption" color="text.secondary">
                                {(doc.file_size / 1024 / 1024).toFixed(2)} MB
                              </Typography>
                            </TableCell>
                            <TableCell>
                              {(doc.document_type === DocumentType.CASE_DOCUMENT ||
                                doc.document_type === DocumentType.REFERENCE) && (
                                <FormControl size="small" fullWidth>
                                  <Select
                                    value={doc.evidence_label || ''}
                                    onChange={(e) => {
                                      const v = e.target.value as string
                                      const next: EvidenceLabel | null = v
                                        ? (v as EvidenceLabel)
                                        : null
                                      updateDocument({ id: doc.id, evidence_label: next })
                                        .unwrap()
                                        .catch((err: any) =>
                                          alert(
                                            `Cập nhật nhãn thất bại: ${err?.data?.detail || err?.message || ''}`,
                                          ),
                                        )
                                    }}
                                    displayEmpty
                                    sx={{ '& .MuiSelect-select': { py: 0.5 } }}
                                    renderValue={(val) =>
                                      val === EvidenceLabel.CORRECT ? (
                                        <Chip size="small" label="✓ Chuẩn" color="success" variant="outlined" />
                                      ) : val === EvidenceLabel.DECOY ? (
                                        <Chip size="small" label="✗ Nhiễu" color="error" variant="outlined" />
                                      ) : (
                                        <Typography variant="caption" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                          Đề bài/Nền
                                        </Typography>
                                      )
                                    }
                                  >
                                    <MenuItem value=""><em>Đề bài / Tài liệu nền</em></MenuItem>
                                    <MenuItem value={EvidenceLabel.CORRECT}>✓ Chứng cứ chuẩn</MenuItem>
                                    <MenuItem value={EvidenceLabel.DECOY}>✗ Bẫy nhiễu</MenuItem>
                                  </Select>
                                </FormControl>
                              )}
                            </TableCell>
                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                              <IconButton size="small" onClick={() => handleDownload(doc.id, doc.file_name)} title="Tải về">
                                <Download fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => openEditDocDialog(doc)} color="primary" title="Sửa metadata">
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" onClick={() => handleDelete(doc.id)} color="error" title="Xóa">
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )
                return (
                  <Box>
                    {poolSummary && (
                      <Alert
                        severity={poolSummary.ready_for_assign ? 'success' : 'warning'}
                        icon={false}
                        sx={{ mb: 2 }}
                      >
                        <Typography variant="caption">
                          <strong>Pool đáp án:</strong>{' '}
                          {poolSummary.correct_count}/{poolSummary.min_correct} chứng cứ CHUẨN ·{' '}
                          {poolSummary.decoy_count}/{poolSummary.min_decoy} BẪY nhiễu{' '}
                          {poolSummary.ready_for_assign
                            ? '— đủ để phân bổ cho đội.'
                            : '— chưa đủ để phân bổ. Tag thêm CORRECT/DECOY cho documents bên dưới.'}
                        </Typography>
                      </Alert>
                    )}
                    {groups.map((g) =>
                      g.docs.length === 0 ? null : (
                        <Box key={g.key} sx={{ mb: 1 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                            <Chip
                              size="small"
                              label={`${g.label} · ${g.docs.length}`}
                              color={g.color}
                              variant="filled"
                            />
                          </Box>
                          {renderTable(g.docs)}
                        </Box>
                      ),
                    )}
                  </Box>
                )
              })()
            )}
          </Box>
        )}

        {/* RAG chỉ dùng cho AI Assistive trong ScoringPage, không cần tab riêng ở đây */}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Đóng</Button>
      </DialogActions>

      {/* Assign Teams Dialog */}
      <Dialog open={assignDialogOpen} onClose={() => setAssignDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Phân bổ đội thi cho cuộc thi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {poolSummary && !poolSummary.ready_for_assign && (
              <Alert severity="error">
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                  Chưa đủ chứng cứ đáp án trong pool đề thi
                </Typography>
                <Typography variant="body2">
                  Chứng cứ CHUẨN: <strong>{poolSummary.correct_count}</strong>/{poolSummary.min_correct} (tối thiểu) ·
                  &nbsp;Bẫy NHIỄU: <strong>{poolSummary.decoy_count}</strong>/{poolSummary.min_decoy} (tối thiểu).
                  Vào tab "Tài liệu" để tag thêm trước khi phân bổ.
                </Typography>
              </Alert>
            )}
            {poolSummary && poolSummary.ready_for_assign && (
              <Alert severity="success" icon={false}>
                <Typography variant="caption">
                  ✓ Pool đáp án: {poolSummary.correct_count} chứng cứ chuẩn + {poolSummary.decoy_count} bẫy nhiễu — đủ để phân bổ.
                </Typography>
              </Alert>
            )}
            <FormControl fullWidth>
              <InputLabel>Chọn đội thi *</InputLabel>
              <Select
                multiple
                value={selectedTeamIds}
                onChange={(e) => setSelectedTeamIds(e.target.value as string[])}
                label="Chọn đội thi *"
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(selected as string[]).map((teamId) => {
                      const team = teams.find(t => t.id === teamId)
                      return team ? (
                        <Chip key={teamId} label={`${team.name} - ${team.university}`} size="small" />
                      ) : null
                    })}
                  </Box>
                )}
              >
                {/* E.1 — chỉ cho phân bổ đội đã duyệt; đội chưa duyệt vẫn show
                    nhưng disabled kèm lý do để Admin biết bị thiếu gì. */}
                {teams.map((team) => {
                  const summary = getTeamRegistrationSummary(team)
                  const isApproved =
                    (team.status === 'active' || team.status === 'auto_approved') && summary.is_complete
                  const statusLabel = getTeamRegistrationLabel(team)
                  return (
                    <MenuItem key={team.id} value={team.id} disabled={!isApproved}>
                      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                        <Typography variant="body2" fontWeight={500}>
                          {team.name} - {team.university}
                        </Typography>
                        {!isApproved && (
                          <Typography variant="caption" color="warning.main">
                            Chưa thể phân bổ: {statusLabel}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  )
                })}
              </Select>
            </FormControl>
            <TextField
              label="Deadline nộp bài"
              type="datetime-local"
              value={assignDeadline}
              onChange={(e) => setAssignDeadline(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
            <Alert severity="info">
              Bạn có thể chọn nhiều đội thi để phân bổ cho cuộc thi này. Các đội thi đã được phân bổ trước đó sẽ được giữ lại.
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>Hủy</Button>
          <Button
            onClick={handleAssignCase}
            variant="contained"
            disabled={isAssigning || (poolSummary ? !poolSummary.ready_for_assign : false)}
            sx={{
              background: gradientBrand,
              '&:hover': {
                background: gradientBrand,
                filter: 'brightness(1.06)',
              },
            }}
          >
            {isAssigning ? <CircularProgress size={20} /> : 'Phân bổ'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Tài liệu cuộc thi</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Tiêu đề *"
              value={uploadForm.title}
              onChange={(e) => setUploadForm({ ...uploadForm, title: e.target.value })}
              required
            />
            <TextField
              fullWidth
              label="Mô tả"
              value={uploadForm.description}
              onChange={(e) => setUploadForm({ ...uploadForm, description: e.target.value })}
              multiline
              rows={3}
            />
            <FormControl fullWidth>
              <InputLabel>Loại tài liệu *</InputLabel>
              <Select
                value={uploadForm.document_type}
                label="Loại tài liệu *"
                onChange={(e) =>
                  setUploadForm({ ...uploadForm, document_type: e.target.value as DocumentType })
                }
              >
                <MenuItem value={DocumentType.CASE_DOCUMENT}>Tài liệu cuộc thi</MenuItem>
                <MenuItem value={DocumentType.REFERENCE}>Tài liệu tham khảo</MenuItem>
              </Select>
            </FormControl>
            <Button variant="outlined" component="label" fullWidth startIcon={<Upload />}>
              Chọn File
              <input type="file" hidden onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setUploadForm({ ...uploadForm, file: e.target.files[0] })
                }
              }} />
            </Button>
            {uploadForm.file && (
              <Typography variant="body2">
                Đã chọn: {uploadForm.file.name} ({(uploadForm.file.size / 1024 / 1024).toFixed(2)} MB)
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleUpload} variant="contained" disabled={isUploading}>
            {isUploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={editDocDialogOpen} onClose={() => setEditDocDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Chỉnh sửa tài liệu</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField
              label="Tiêu đề *"
              value={editDocForm?.title || ''}
              onChange={(e) =>
                setEditDocForm((prev) => (prev ? { ...prev, title: e.target.value } : prev))
              }
              fullWidth
            />
            <TextField
              label="Mô tả"
              value={editDocForm?.description || ''}
              onChange={(e) =>
                setEditDocForm((prev) => (prev ? { ...prev, description: e.target.value } : prev))
              }
              multiline
              rows={3}
              fullWidth
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDocDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleUpdateDoc} variant="contained" disabled={isUpdatingDoc}>
            {isUpdatingDoc ? <CircularProgress size={20} /> : 'Lưu'}
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

// RAG Search Tab Component – dùng RTK Query (useSearchDocumentsMutation, useSearchDocumentsScoringMutation)
function RAGSearchTab({
  caseItem,
  teams,
  canScoreCases,
}: {
  caseItem: Case
  teams: any[]
  canScoreCases: boolean
}) {
  const [searchQuery, setSearchQuery] = useState('')
  const [teamId, setTeamId] = useState('')
  const [ragTab, setRagTab] = useState(0)
  const [results, setResults] = useState<any[]>([])
  const [isSearching, setIsSearching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [searchDocuments] = useSearchDocumentsMutation()
  const [searchDocumentsScoring] = useSearchDocumentsScoringMutation()

  const handleSearch = async (context: 'case' | 'team' | 'scoring') => {
    if (!searchQuery.trim()) {
      setError('Vui lòng nhập từ khóa tìm kiếm')
      return
    }
    if (context === 'scoring' && !teamId.trim()) {
      setError('Vui lòng chọn đội thi để chấm điểm')
      return
    }

    setError(null)
    setIsSearching(true)
    try {
      let data: any[]
      if (context === 'case') {
        data = await searchDocuments({
          query: searchQuery,
          case_id: caseItem.id,
          limit: 10,
          similarity_threshold: 0.7,
        }).unwrap()
      } else if (context === 'team') {
        data = await searchDocuments({
          query: searchQuery,
          case_id: caseItem.id,
          team_id: teamId || undefined,
          limit: 10,
          similarity_threshold: 0.7,
        }).unwrap()
      } else {
        data = await searchDocumentsScoring({
          query: searchQuery,
          case_id: caseItem.id,
          team_id: teamId,
          limit: 20,
          similarity_threshold: 0.7,
          case_weight: 0.5,
          team_weight: 0.5,
        }).unwrap()
      }
      setResults(Array.isArray(data) ? data : [])
    } catch (err: any) {
      setError(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi tìm kiếm')
      setResults([])
    } finally {
      setIsSearching(false)
    }
  }

  const getDocumentTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      case_document: 'Tài liệu cuộc thi',
      reference: 'Tài liệu tham khảo',
      memorial: 'Hồ sơ khởi kiện',
      evidence: 'Chứng cứ',
      transcript: 'Transcript',
      rubric: 'Rubric',
    }
    return labels[type] || type
  }

  const assignedTeamIds = caseItem.assigned_team_ids || []

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        RAG Search - Tìm kiếm Tài liệu
      </Typography>

      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            fullWidth
            label="Từ khóa tìm kiếm"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Ví dụ: Yêu cầu của nguyên đơn, Phân tích pháp lý..."
            multiline
            rows={2}
          />
          {ragTab === 1 && (
            <FormControl fullWidth>
              <InputLabel>Đội thi (tùy chọn)</InputLabel>
              <Select
                value={teamId}
                label="Đội thi (tùy chọn)"
                onChange={(e) => setTeamId(e.target.value)}
              >
                <MenuItem value="">Tất cả các đội</MenuItem>
                {assignedTeamIds.map((tid: string) => {
                  const team = teams.find((t) => t.id === tid)
                  return team ? (
                    <MenuItem key={tid} value={tid}>
                      {team.name} - {team.university}
                    </MenuItem>
                  ) : null
                })}
              </Select>
            </FormControl>
          )}
          {ragTab === 2 && (
            <FormControl fullWidth required>
              <InputLabel>Đội thi *</InputLabel>
              <Select
                value={teamId}
                label="Đội thi *"
                onChange={(e) => setTeamId(e.target.value)}
                required
              >
                <MenuItem value="">Chọn đội thi</MenuItem>
                {assignedTeamIds.map((tid: string) => {
                  const team = teams.find((t) => t.id === tid)
                  return team ? (
                    <MenuItem key={tid} value={tid}>
                      {team.name} - {team.university}
                    </MenuItem>
                  ) : null
                })}
              </Select>
            </FormControl>
          )}
          <Button
            variant="contained"
            startIcon={<Search />}
            onClick={() => {
              if (ragTab === 0) handleSearch('case')
              else if (ragTab === 1) handleSearch('team')
              else handleSearch('scoring')
            }}
            disabled={isSearching || (ragTab === 2 && !teamId)}
          >
            {isSearching ? <CircularProgress size={20} /> : 'Tìm kiếm'}
          </Button>
        </Box>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Paper>
        <Tabs value={ragTab} onChange={(_, v) => setRagTab(v)}>
          <Tab label="Tài liệu cuộc thi (RAG 1)" />
          <Tab label="Tài liệu đội thi (RAG 2)" />
          {canScoreCases && <Tab label="Chấm điểm (RAG 3)" />}
        </Tabs>

        <Box sx={{ p: 2 }}>
          {ragTab === 0 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tìm kiếm trong tài liệu cuộc thi và tài liệu tham khảo (CASE_DOCUMENT, REFERENCE)
              </Typography>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}

          {ragTab === 1 && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Tìm kiếm trong memorial, evidence, transcript từ các team (MEMORIAL, EVIDENCE, TRANSCRIPT)
              </Typography>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}

          {ragTab === 2 && canScoreCases && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Kết hợp tìm kiếm từ cả tài liệu cuộc thi và tài liệu đội thi để hỗ trợ chấm điểm
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Yêu cầu quyền SCORE_CASES (Judge/Secretary only)
              </Alert>
              {isSearching && <LinearProgress sx={{ mb: 2 }} />}
              <RAGSearchResults results={results} getDocumentTypeLabel={getDocumentTypeLabel} />
            </Box>
          )}
        </Box>
      </Paper>
    </Box>
  )
}

// RAG Search Results Component
function RAGSearchResults({
  results,
  getDocumentTypeLabel,
}: {
  results: any[]
  getDocumentTypeLabel: (type: string) => string
}) {
  if (results.length === 0) {
    return (
      <Box sx={{ p: 4, textAlign: 'center' }}>
        <Typography variant="body1" color="text.secondary">
          Chưa có kết quả tìm kiếm. Hãy nhập từ khóa và nhấn "Tìm kiếm"
        </Typography>
      </Box>
    )
  }

  return (
    <Grid container spacing={2}>
      {results.map((result, index) => (
        <Grid item xs={12} key={result.chunk_id || index}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                <Box>
                  <Typography variant="h6" component="h3">
                    {result.document_title}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                    <Chip
                      label={getDocumentTypeLabel(result.metadata?.document_type || '')}
                      size="small"
                      color="primary"
                    />
                    <Chip
                      label={`Similarity: ${(result.similarity_score * 100).toFixed(1)}%`}
                      size="small"
                      color="success"
                    />
                  </Box>
                </Box>
              </Box>
              <Divider sx={{ my: 2 }} />
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'pre-wrap' }}>
                {result.content}
              </Typography>
              {result.metadata && (
                <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {result.metadata.case_id && (
                    <Chip label={`Case: ${result.metadata.case_id}`} size="small" variant="outlined" />
                  )}
                  {result.metadata.team_id && (
                    <Chip label={`Team: ${result.metadata.team_id}`} size="small" variant="outlined" />
                  )}
                </Box>
              )}
            </CardContent>
            <CardActions>
              <Button size="small" startIcon={<Description />}>
                Xem tài liệu
              </Button>
              <Button size="small" startIcon={<Download />}>
                Download
              </Button>
            </CardActions>
          </Card>
        </Grid>
      ))}
    </Grid>
  )
}

// Case Rounds Tab Component
function CaseRoundsTab({ caseId, caseItem }: { caseId: string; caseItem: Case }) {
  const { user: currentUser } = useAppSelector((state) => state.auth)
  const { data: rounds = [], isLoading, refetch } = useGetCaseRoundsQuery(caseId)
  const { data: currentCase, refetch: refetchCase } = useGetCaseQuery(caseId)
  const [updateRound, { isLoading: isUpdating }] = useUpdateRoundMutation()
  const [moveToNextRound, { isLoading: isMoving }] = useMoveToNextRoundMutation()
  const [moveToRound, { isLoading: isMovingToRound }] = useMoveToRoundMutation()

  const [openDialog, setOpenDialog] = useState(false)
  const [editingRound, setEditingRound] = useState<Round | null>(null)
  const [openRoundSelectDialog, setOpenRoundSelectDialog] = useState(false)
  const [movingToRoundId, setMovingToRoundId] = useState<string | null>(null) // Track which round is being moved to
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  const canManageRounds = !!(currentUser && hasPermission(currentUser.role, 'ASSIGN_CASES'))
  
  // Sắp xếp rounds theo trình tự: TRAINING -> MEMORIAL -> SEMIFINAL -> FINAL
  const roundOrder = [RoundType.TRAINING, RoundType.MEMORIAL, RoundType.SEMIFINAL, RoundType.FINAL]
  const sortedRounds = rounds.length > 0 
    ? [...rounds].sort((a, b) => roundOrder.indexOf(a.round_type) - roundOrder.indexOf(b.round_type))
    : []
  
  // Tìm vòng hiện tại
  const currentRoundId = currentCase?.current_round_id || caseItem.current_round_id
  const currentRoundIndex = sortedRounds.findIndex(r => r.id === currentRoundId)
  const canMoveToNext = currentRoundIndex >= 0 && currentRoundIndex < sortedRounds.length - 1
  const canMoveToPrevious = currentRoundIndex > 0

  const ROUND_TYPE_LABELS: Record<RoundType, string> = {
    [RoundType.TRAINING]: 'Vòng đào tạo',
    [RoundType.MEMORIAL]: 'Vòng Sơ loại (chấm hồ sơ)',
    [RoundType.SEMIFINAL]: 'Vòng Bán kết',
    [RoundType.FINAL]: 'Vòng Chung kết',
  }

  const handleOpenDialog = (round: Round) => {
    setEditingRound(round)
    setOpenDialog(true)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleCloseDialog = () => {
    setOpenDialog(false)
    setEditingRound(null)
    setErrorMessage(null)
    setSuccessMessage(null)
  }

  const handleUpdateRound = async (roundId: string, updates: { start_date?: string; end_date?: string; status?: RoundStatus; meeting_link?: string }) => {
    // Bug fix 2026-05-24: KHÔNG nuốt error — dialog handleSubmit cần exception
    // để biết update fail, hiển thị message trong dialog + giữ dialog mở. Trước
    // fix: try/catch ở đây swallow error → dialog tưởng thành công → close →
    // user thấy như "click không có gì xảy ra" + data không cập nhật.
    await updateRound({
      id: roundId,
      start_date: updates.start_date,
      end_date: updates.end_date,
      status: updates.status,
      meeting_link: updates.meeting_link,
    }).unwrap()
    setSuccessMessage('Cập nhật vòng thi thành công!')
    refetch()
  }

  const handleMoveToRound = async (roundId: string) => {
    setMovingToRoundId(roundId)
    setErrorMessage(null)
    try {
      await moveToRound({ caseId, roundId }).unwrap()
      setSuccessMessage('Đã chuyển vòng thành công!')
      setOpenRoundSelectDialog(false)
      setMovingToRoundId(null)
      refetch()
      refetchCase()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi chuyển vòng')
      setMovingToRoundId(null)
    }
  }

  const handleMoveToPreviousRound = async () => {
    if (!canMoveToPrevious || currentRoundIndex <= 0 || isMovingToRound) return
    
    const previousRound = sortedRounds[currentRoundIndex - 1]
    await handleMoveToRound(previousRound.id)
  }

  const handleMoveToNextRound = async () => {
    if (!canMoveToNext || isMoving) return
    
    setErrorMessage(null)
    try {
      await moveToNextRound(caseId).unwrap()
      setSuccessMessage('Đã chuyển sang vòng tiếp theo!')
      refetch()
      refetchCase()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra khi chuyển vòng')
    }
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Quản lý Vòng thi</Typography>
        {canManageRounds && (
          <Box sx={{ display: 'flex', gap: 1 }}>
            {canMoveToPrevious && (
              <Button
                variant="outlined"
                startIcon={<ArrowBack />}
                onClick={handleMoveToPreviousRound}
                disabled={isMovingToRound}
                color="secondary"
              >
                {isMovingToRound ? <CircularProgress size={20} /> : 'Về vòng trước'}
              </Button>
            )}
            <Button
              variant="outlined"
              startIcon={isMovingToRound ? <CircularProgress size={16} /> : <SwapHoriz />}
              onClick={() => setOpenRoundSelectDialog(true)}
              disabled={isMovingToRound || isMoving}
            >
              Chọn vòng
            </Button>
            {canMoveToNext && (
              <Button
                variant="contained"
                startIcon={<ArrowForward />}
                onClick={handleMoveToNextRound}
                disabled={isMoving}
                sx={{
                  background: gradientBrand,
                  '&:hover': {
                    background: gradientBrand,
                    filter: 'brightness(1.06)',
                  },
                }}
              >
                {isMoving ? <CircularProgress size={20} /> : 'Vòng tiếp theo'}
              </Button>
            )}
          </Box>
        )}
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 4 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rectangular" height={120} sx={{ borderRadius: 2 }} />
          ))}
        </Box>
      ) : sortedRounds.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Timeline sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            Chưa có vòng thi nào
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Các vòng thi sẽ được tạo tự động khi tạo cuộc thi
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {sortedRounds.map((round, index) => {
            const isCurrentRound = round.id === currentRoundId
            const isPastRound = currentRoundIndex >= 0 && index < currentRoundIndex
            const isFutureRound = currentRoundIndex >= 0 && index > currentRoundIndex
            
            return (
              <Grid item xs={12} md={6} key={round.id}>
                <Card 
                  elevation={isCurrentRound ? 4 : 2} 
                  sx={{ 
                    height: '100%',
                    border: isCurrentRound ? 2 : 0,
                    borderColor: isCurrentRound ? 'primary.main' : 'transparent',
                    backgroundColor: isCurrentRound ? 'action.selected' : 'background.paper',
                    position: 'relative',
                    transition: 'all 0.2s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: 4,
                    },
                  }}
                >
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 2 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                        <Typography variant="h6" fontWeight={600}>
                          {round.name}
                        </Typography>
                        {isCurrentRound && (
                          <Chip 
                            label="Vòng hiện tại" 
                            color="primary" 
                            size="small"
                            sx={{ fontWeight: 'bold' }}
                          />
                        )}
                        {isPastRound && (
                          <Chip 
                            label="Đã hoàn thành" 
                            color="default" 
                            size="small"
                          />
                        )}
                        {isFutureRound && (
                          <Chip 
                            label="Sắp tới" 
                            color="info" 
                            size="small"
                          />
                        )}
                      </Box>
                    </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" color="text.secondary">
                        Loại:
                      </Typography>
                      <Chip label={ROUND_TYPE_LABELS[round.round_type]} size="small" variant="outlined" />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CalendarToday fontSize="small" color="action" />
                      <Typography variant="body2">
                        {new Date(round.start_date).toLocaleDateString('vi-VN')} -{' '}
                        {new Date(round.end_date).toLocaleDateString('vi-VN')}
                      </Typography>
                    </Box>
                  </Box>

                  {round.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {round.description.length > 100 ? `${round.description.substring(0, 100)}...` : round.description}
                    </Typography>
                  )}

                  {/* Courtroom Info for SEMIFINAL rounds */}
                  {round.round_type === RoundType.SEMIFINAL && (
                    <CourtroomCard roundId={round.id} canManageRounds={!!canManageRounds} />
                  )}

                    {canManageRounds && (
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <IconButton 
                          size="small" 
                          onClick={() => handleOpenDialog(round)} 
                          color="primary"
                          title="Chỉnh sửa thời gian và trạng thái"
                        >
                          <Edit />
                        </IconButton>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            )
          })}
        </Grid>
      )}

      {/* Edit Round Dialog - Chỉ cho phép chỉnh sửa thời gian và trạng thái */}
      {editingRound && (
        <EditRoundDialog
          open={openDialog}
          onClose={handleCloseDialog}
          round={editingRound}
          onUpdate={handleUpdateRound}
          isUpdating={isUpdating}
        />
      )}

      {/* Round Selection Dialog */}
      <Dialog
        open={openRoundSelectDialog}
        onClose={() => !isMovingToRound && setOpenRoundSelectDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Chọn vòng thi để chuyển đến</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
            <Alert severity="info">
              Chọn vòng thi bạn muốn chuyển đến. Bạn có thể chuyển về vòng trước hoặc sang vòng sau bất kỳ.
            </Alert>
            {sortedRounds.map((round, index) => {
              const isCurrentRound = round.id === currentRoundId
              const isPastRound = currentRoundIndex >= 0 && index < currentRoundIndex
              const isFutureRound = currentRoundIndex >= 0 && index > currentRoundIndex
              const isMovingToThisRound = movingToRoundId === round.id
              
              return (
                <Card
                  key={round.id}
                  sx={{
                    cursor: isCurrentRound || isMovingToRound ? 'default' : 'pointer',
                    border: isCurrentRound ? 2 : 1,
                    borderColor: isCurrentRound ? 'primary.main' : isMovingToThisRound ? 'primary.main' : 'divider',
                    backgroundColor: isCurrentRound ? 'action.selected' : isMovingToThisRound ? 'action.hover' : 'background.paper',
                    opacity: isMovingToRound && !isMovingToThisRound ? 0.5 : 1,
                    position: 'relative',
                    '&:hover': isCurrentRound || isMovingToRound ? {} : {
                      backgroundColor: 'action.hover',
                      borderColor: 'primary.main',
                    },
                    transition: 'all 0.2s ease-in-out',
                  }}
                  onClick={() => !isCurrentRound && !isMovingToRound && handleMoveToRound(round.id)}
                >
                  {isMovingToThisRound && (
                    <Box
                      sx={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: 'rgba(255, 255, 255, 0.8)',
                        zIndex: 1,
                      }}
                    >
                      <CircularProgress size={40} />
                    </Box>
                  )}
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Box sx={{ flex: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                          <Typography variant="h6">
                            {ROUND_TYPE_LABELS[round.round_type]}
                          </Typography>
                          {isMovingToThisRound && (
                            <CircularProgress size={16} />
                          )}
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {round.name}
                        </Typography>
                        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                          {isCurrentRound && (
                            <Chip label="Vòng hiện tại" size="small" color="primary" />
                          )}
                          {isPastRound && (
                            <Chip label="Đã hoàn thành" size="small" color="default" />
                          )}
                          {isFutureRound && (
                            <Chip label="Sắp tới" size="small" color="info" />
                          )}
                        </Box>
                      </Box>
                      {isCurrentRound && (
                        <Chip label="Đang chọn" color="primary" />
                      )}
                    </Box>
                  </CardContent>
                </Card>
              )
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setOpenRoundSelectDialog(false)}
            disabled={isMovingToRound}
          >
            Hủy
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

// Edit Round Dialog Component - Chỉ cho phép chỉnh sửa thời gian và trạng thái
function EditRoundDialog({
  open,
  onClose,
  round,
  onUpdate,
  isUpdating,
}: {
  open: boolean
  onClose: () => void
  round: Round
  onUpdate: (roundId: string, updates: { start_date?: string; end_date?: string; status?: RoundStatus; meeting_link?: string }) => Promise<void>
  isUpdating: boolean
}) {
  // Helper để chuyển ISO string (UTC) thành chuỗi local dùng cho thẻ <input type="datetime-local">
  const toLocalIsoString = (isoDateStr?: string | null) => {
    if (!isoDateStr) return ''
    const date = new Date(isoDateStr)
    if (isNaN(date.getTime())) return ''
    const pad = (n: number) => n.toString().padStart(2, '0')
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
  }

  const [startDate, setStartDate] = useState(toLocalIsoString(round.start_date))
  const [endDate, setEndDate] = useState(toLocalIsoString(round.end_date))
  const [status, setStatus] = useState<RoundStatus>(round.status)
  const [meetingLink, setMeetingLink] = useState<string>(round.meeting_link || '')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const STATUS_LABELS: Record<RoundStatus, string> = {
    [RoundStatus.UPCOMING]: 'Sắp diễn ra',
    [RoundStatus.ACTIVE]: 'Đang diễn ra',
    [RoundStatus.COMPLETED]: 'Đã hoàn thành',
    [RoundStatus.CANCELLED]: 'Đã hủy',
  }

  const handleSubmit = async () => {
    setErrorMessage(null)

    if (!startDate || !endDate) {
      setErrorMessage('Ngày bắt đầu và ngày kết thúc là bắt buộc')
      return
    }

    if (new Date(startDate) >= new Date(endDate)) {
      setErrorMessage('Ngày kết thúc phải sau ngày bắt đầu')
      return
    }

    try {
      await onUpdate(round.id, {
        start_date: new Date(startDate).toISOString(),
        end_date: new Date(endDate).toISOString(),
        status,
        meeting_link: meetingLink || undefined,
      })
      onClose()
    } catch (err: any) {
      setErrorMessage(err?.data?.detail || err?.message || 'Có lỗi xảy ra')
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Chỉnh sửa vòng thi: {round.name}</DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
          {errorMessage && (
            <Alert severity="error" onClose={() => setErrorMessage(null)}>
              {errorMessage}
            </Alert>
          )}
          <Alert severity="info">
            Chỉ có thể chỉnh sửa thời gian, trạng thái và link phòng trực tuyến của vòng thi. Tên và loại vòng thi không thể thay đổi.
          </Alert>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Ngày bắt đầu"
              type="datetime-local"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
            <TextField
              label="Ngày kết thúc"
              type="datetime-local"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              fullWidth
              required
            />
          </Box>
          <FormControl fullWidth>
            <InputLabel>Trạng thái</InputLabel>
            <Select
              value={status}
              onChange={(e) => setStatus(e.target.value as RoundStatus)}
              label="Trạng thái"
            >
              <MenuItem value={RoundStatus.UPCOMING}>{STATUS_LABELS[RoundStatus.UPCOMING]}</MenuItem>
              <MenuItem value={RoundStatus.ACTIVE}>{STATUS_LABELS[RoundStatus.ACTIVE]}</MenuItem>
              <MenuItem value={RoundStatus.COMPLETED}>{STATUS_LABELS[RoundStatus.COMPLETED]}</MenuItem>
              <MenuItem value={RoundStatus.CANCELLED}>{STATUS_LABELS[RoundStatus.CANCELLED]}</MenuItem>
            </Select>
          </FormControl>
          {round.round_type === RoundType.SEMIFINAL && (
            <TextField
              label="Daily Room Link"
              value={meetingLink}
              onChange={(e) => setMeetingLink(e.target.value)}
              placeholder="https://your-team.daily.co/phien-toa-xxx"
              fullWidth
              helperText="Link phòng trực tuyến (Daily) có thể tự động tạo hoặc chỉnh thủ công nếu cần."
            />
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Hủy</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={isUpdating}
          sx={{
            background: gradientBrand,
            '&:hover': {
              background: gradientBrand,
              filter: 'brightness(1.06)',
            },
          }}
        >
          {isUpdating ? <CircularProgress size={20} /> : 'Cập nhật'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

// Courtroom Card Component for SEMIFINAL rounds
function CourtroomCard({ roundId, canManageRounds }: { roundId: string; canManageRounds: boolean }) {
  const { data: courtroom, isLoading: isLoadingCourtroom, refetch: refetchCourtroom } = useGetCourtroomByRoundQuery(roundId, {
    skip: false, // Always fetch
  })
  const [joinCourtroom, { isLoading: isJoining }] = useJoinCourtroomMutation()
  const [endCourtroom, { isLoading: isEnding }] = useEndCourtroomMutation()
  const [createCourtroom, { isLoading: isCreatingCr }] = useCreateCourtroomMutation()
  const [createCrError, setCreateCrError] = useState<string | null>(null)

  const handleCreateCourtroom = async () => {
    setCreateCrError(null)
    try {
      await createCourtroom({ round_id: roundId, auto_create_meeting: true }).unwrap()
      refetchCourtroom()
    } catch (e: any) {
      setCreateCrError(e?.data?.detail || e?.message || 'Tạo phòng thất bại')
    }
  }

  const handleJoinCourtroom = async () => {
    if (!courtroom) return
    try {
      const result = await joinCourtroom(courtroom.id).unwrap()
      if (result.meeting_link) {
        window.open(result.meeting_link, '_blank')
      }
      refetchCourtroom()
    } catch (error: any) {
      console.error('Failed to join courtroom:', error)
    }
  }

  const handleEndCourtroom = async () => {
    if (!courtroom) return
    if (!confirm('Bạn có chắc muốn kết thúc courtroom này?')) return
    try {
      await endCourtroom({
        courtroomId: courtroom.id,
        result: 'postponed',
        conclusion: 'Kết thúc phiên từ trang quản lý vụ án.',
      }).unwrap()
      refetchCourtroom()
    } catch (error: any) {
      console.error('Failed to end courtroom:', error)
    }
  }

  if (isLoadingCourtroom) {
    return <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 1, mb: 2 }} />
  }

  if (!courtroom) {
    // Bug fix 2026-05-24: nếu round đã ACTIVE (Vòng hiện tại) nhưng courtroom
    // chưa tạo (ví dụ status flip thẳng qua EditRoundDialog không qua
    // move_to_round), cho phép BTC tạo phòng thủ công thay vì chỉ thấy alert.
    return (
      <Box sx={{ mb: 2 }}>
        <Alert severity="info" sx={{ mb: canManageRounds ? 1 : 0 }}>
          Courtroom chưa được tạo. Courtroom sẽ được tạo tự động khi chuyển sang vòng này.
        </Alert>
        {createCrError && (
          <Alert severity="error" sx={{ mb: 1 }} onClose={() => setCreateCrError(null)}>
            {createCrError}
          </Alert>
        )}
        {canManageRounds && (
          <Button
            variant="contained"
            size="small"
            startIcon={<VideoCall />}
            onClick={handleCreateCourtroom}
            disabled={isCreatingCr}
            sx={{
              background: gradientBrand,
              '&:hover': { background: gradientBrand, filter: 'brightness(1.06)' },
            }}
          >
            {isCreatingCr ? <CircularProgress size={16} /> : 'Tạo phòng ngay'}
          </Button>
        )}
      </Box>
    )
  }

  const STATUS_LABELS: Record<CourtroomStatus, string> = {
    [CourtroomStatus.SCHEDULED]: 'Đã lên lịch',
    [CourtroomStatus.ACTIVE]: 'Đang diễn ra',
    [CourtroomStatus.COMPLETED]: 'Đã kết thúc',
    [CourtroomStatus.CANCELLED]: 'Đã hủy',
  }

  const STATUS_COLORS: Record<CourtroomStatus, 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'> = {
    [CourtroomStatus.SCHEDULED]: 'info',
    [CourtroomStatus.ACTIVE]: 'success',
    [CourtroomStatus.COMPLETED]: 'default',
    [CourtroomStatus.CANCELLED]: 'error',
  }

  return (
    <Paper sx={{ p: 2, mb: 2, backgroundColor: 'action.hover' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VideoCall fontSize="small" color="primary" />
          <Typography variant="subtitle2" fontWeight={600}>
            Phiên toà ảo
          </Typography>
        </Box>
        <Chip
          label={STATUS_LABELS[courtroom.status]}
          color={STATUS_COLORS[courtroom.status]}
          size="small"
        />
      </Box>

      {courtroom.meeting_link &&
        (courtroom.meeting_link || '').toLowerCase().includes('daily.co') ? (
          <Alert severity="info" sx={{ mb: 1 }}>
            Video Daily chỉ tham gia qua trang{' '}
            <Button
              component={RouterLink}
              to="/courtroom"
              size="small"
              variant="outlined"
              sx={{ textTransform: 'none', mx: 0.5, verticalAlign: 'middle' }}
            >
              Phiên toà ảo
            </Button>
            (nhúng trong eCourt). Không mở phòng Daily ở tab mới từ trang vụ án.
          </Alert>
        ) : (
          <Box sx={{ mb: 1 }}>
            <Button
              variant="contained"
              size="small"
              startIcon={<OpenInNew />}
              onClick={handleJoinCourtroom}
              disabled={isJoining || courtroom.status === CourtroomStatus.COMPLETED}
              sx={{
                background: gradientBrand,
                '&:hover': {
                  background: gradientBrand,
                  filter: 'brightness(1.06)',
                },
              }}
            >
              {isJoining ? <CircularProgress size={16} /> : 'Mở link phòng (tab mới)'}
            </Button>
          </Box>
        )}

      {courtroom.participants && courtroom.participants.length > 0 && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <People fontSize="small" color="action" />
          <Typography variant="caption" color="text.secondary">
            {courtroom.participants.length} người tham gia
          </Typography>
        </Box>
      )}

      {courtroom.recording_url && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            startIcon={<Download />}
            href={courtroom.recording_url}
            target="_blank"
            variant="outlined"
          >
            Tải Recording
          </Button>
        </Box>
      )}

      {canManageRounds && courtroom.status === CourtroomStatus.ACTIVE && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            color="error"
            variant="outlined"
            onClick={handleEndCourtroom}
            disabled={isEnding}
          >
            {isEnding ? <CircularProgress size={16} /> : 'Kết thúc Courtroom'}
          </Button>
        </Box>
      )}
    </Paper>
  )
}
